// docs/92 HM6, docs/94, docs/95, docs/116: which moment a huge body's move plays on the board, by its
// people and the move's name (the sim's moves 95–165 and 299–312, the movement cards and its kit's own
// blows). Pure, so the tests can read it.
import type { HugeArt, HugeKind } from '../hugeArt';
import type { SfxKey } from '../../core/audio/keys.gen';

/** The voice it roars and gloats in (fk-sfx `Voice`: its reveal and the taunt over a fallen hero). */
export const HUGE_TAUNT: Readonly<Record<HugeKind, SfxKey>> = {
  dragon: 'voice.dragon_taunt',
  troll: 'voice.giant_taunt',
  yeti: 'voice.yeti_taunt',
  worm: 'voice.worm_taunt',
  spider: 'voice.broodmother_taunt',
  serpent: 'voice.basilisk_taunt',
  wyrm: 'voice.wyrm_taunt',
  eagle: 'voice.eagle_taunt',
  ent: 'voice.treant_taunt',
  giant: 'voice.jotun_taunt',
  kraken: 'voice.kraken_taunt',
  maw: 'voice.maw_taunt',
};

/**
 * docs/116: what the deep's two bring up with them on their reveal, under the braam and before
 * their voice (the others reveal on the braam and their voice alone).
 */
export const HUGE_REVEAL: Readonly<Partial<Record<HugeKind, SfxKey>>> = {
  kraken: 'move.tide',
  maw: 'move.abyss',
};

/** The moments a huge body plays. */
export type HugeMoment =
  // the red dragon and the mountain troll (docs/92)
  | 'breath' | 'wings' | 'roar' | 'smash' | 'avalanche'
  // the yeti: a blizzard cone, a bounding pounce, rime closing over its hide
  | 'frost' | 'pounce' | 'rime'
  // the sandworm: a sand vortex swallowing, a geyser of sand bursting up, the dunes heaving, a blast of grit
  | 'engulf' | 'erupt' | 'quake' | 'sandblast'
  // the spider queen: a web line and net, a boil of spiderlings, venom raining down
  | 'web' | 'brood' | 'venom'
  // any fanged one: a lunge and a bite
  | 'bite'
  // the basilisk: the petrifying gaze, coils closing on a foe, its old skin sloughing off
  | 'gaze' | 'coil' | 'shed'
  // the black dragon: a long line of black fire, cinders falling, smoke billowing, the light going out
  | 'shadowflame' | 'cinders' | 'smoke' | 'eclipse'
  // the great eagle (docs/95): a stoop out of the sky, a gale off its wings, a screech
  | 'stoop' | 'gale' | 'screech'
  // the ent: roots bursting up round the foe, a line of thorns, a log hurled, a storm of leaves
  | 'roots' | 'thorns' | 'uproot' | 'leafstorm'
  // the grim giant: a wide sweep of its club, the ground split in a line, its runes flaring
  | 'cleave' | 'earthsplit' | 'runes'
  // the kraken (docs/116): a wave surging out to the foe into a whirlpool, a cloud of ink, an arm
  // lashing out and dragging back, a severed arm writhing back out of the stump
  | 'tide' | 'ink' | 'grasp' | 'regrow'
  // the maw of the deep: jaws snapping shut on the foe, a violet roar out of the abyss, its tongue
  // shooting out and reeling back, sinking under the black water, every eye turning on one hero
  | 'gulp' | 'abyss' | 'tongue' | 'sink' | 'eyes';

/** Which moment a move plays, by its people and its name (docs/92, docs/94, docs/116: the sim's moves and the kit's own). */
export function momentOf(kind: HugeArt['kind'], moveName: string): HugeMoment {
  const n = moveName.toLowerCase();
  switch (kind) {
    case 'dragon':
      if (/breath|flame|fire|scorch|molten|wrath|gout/.test(n)) return 'breath';
      if (/wing|dive|swoop|buffet/.test(n)) return 'wings';
      return 'roar';
    case 'troll':
      // "Mountain Blood" is its wounds closing, not a rockfall: the roar first
      if (/roar|bellow|blood/.test(n)) return 'roar';
      if (/avalanche|mountain|boulder|rock/.test(n)) return 'avalanche';
      return 'smash';
    case 'yeti':
      // Blizzard Breath, Whiteout; Snowblind Pounce, Snatch; Hoarfrost Slam, Frost Maul; Hurled Ice;
      // Rime-Crusted Hide, Thick Pelt; Fury of the Peaks
      if (/fury|howl|roar/.test(n)) return 'roar';
      if (/pounce|snatch|leap|bound/.test(n)) return 'pounce';
      if (/blizzard|whiteout|breath|storm/.test(n)) return 'frost';
      if (/hide|pelt|rime/.test(n)) return 'rime';
      if (/hurl|ice|throw/.test(n)) return 'avalanche';
      return 'smash';
    case 'worm':
      // Devour, Swallowing Sands; Erupting Ambush, Erupt, Sand Spout; Sandblast; Gnash; Thrashing
      // Coils; Sand-Scoured Plates, Death Throes
      if (/devour|swallow|engulf|gulp/.test(n)) return 'engulf';
      if (/erupt|spout|ambush|burrow/.test(n)) return 'erupt';
      if (/sandblast|blast/.test(n)) return 'sandblast';
      if (/gnash|bite|maw/.test(n)) return 'bite';
      if (/thrash|coil|lash/.test(n)) return 'smash';
      return 'quake';
    case 'spider':
      // Venom Rain, Venom Barbs, Feed; Cocoon, Canopy of Webs, Web Shot, Reel in; Call the Brood,
      // Burst the Egg Sacs, Mother's Wrath; Queen's Fangs
      if (/brood|egg|mother|wrath/.test(n)) return 'brood';
      if (/web|cocoon|reel|silk/.test(n)) return 'web';
      if (/fang|bite/.test(n)) return 'bite';
      return 'venom';
    case 'serpent':
      // Petrifying Gaze, Stone Curse, Gaze Returned; Serpent Strike, Venom Fangs; Coil Crush,
      // Constrict; Death Rattle; Shed Skin; Venom Spit
      if (/gaze|stone|petrif/.test(n)) return 'gaze';
      if (/coil|constrict|crush/.test(n)) return 'coil';
      if (/shed|skin/.test(n)) return 'shed';
      if (/rattle|hiss/.test(n)) return 'roar';
      if (/spit|venom rain/.test(n)) return 'venom';
      return 'bite';
    case 'wyrm':
      // Shadowflame Breath, Black Flame; Rain of Cinders; Eclipse; Night Swoop, Night raid; Pall of
      // Ash, Smouldering Hide, Wreathed in Smoke; Rend
      if (/eclipse/.test(n)) return 'eclipse';
      if (/cinder|rain/.test(n)) return 'cinders';
      if (/breath|flame|fire/.test(n)) return 'shadowflame';
      if (/swoop|raid|wing|dive/.test(n)) return 'wings';
      if (/ash|smoke|smoulder|hide|pall/.test(n)) return 'smoke';
      if (/rend|claw|bite|fang/.test(n)) return 'bite';
      return 'roar';
    case 'eagle':
      // Storm of Talons, Talons; Stoop, Diving Rake, Death from Above, Carry off / Carries off <name>;
      // Soar Aloft, Buffeting Wings; Feather Gale; Keen Eye
      if (/stoop|div|above|carr|storm/.test(n)) return 'stoop';
      if (/gale|feather/.test(n)) return 'gale';
      if (/soar|wing|buffet/.test(n)) return 'wings';
      if (/eye|screech|cry|scream/.test(n)) return 'screech';
      return 'bite';
    case 'ent':
      // Strangling Roots, Deep Roots, Overgrow; Uproot, Hurled Log; Wall of Thorns, Thorned Bark;
      // Wrath of the Wood, Last Oak's Fury; Bough Smash
      if (/uproot|log|hurl|tree/.test(n)) return 'uproot';
      if (/thorn|bramble|briar/.test(n)) return 'thorns';
      if (/root|overgrow|strangl/.test(n)) return 'roots';
      if (/wrath|fury|leaf|storm/.test(n)) return 'leafstorm';
      return 'smash';
    case 'giant':
      // Earthsplitter; Sweeping blow, Wrath of the Mountain King; Runefall, Rune of Warding, Grim
      // Endurance; Bellow of the Hold; Giant's Stride; Rune Club; Hurled Boulder
      if (/earth|split/.test(n)) return 'earthsplit';
      if (/club|rune club/.test(n)) return 'smash';
      if (/rune|endur|ward/.test(n)) return 'runes';
      if (/bellow|roar/.test(n)) return 'roar';
      if (/boulder|hurl|rock/.test(n)) return 'avalanche';
      if (/stride|stomp/.test(n)) return 'smash';
      return 'cleave';
    case 'kraken':
      // Rise from the deep / Drags <name> into the deep (the card first: a hero's name is in it);
      // Drag to the Depths, Whirlpool; Ink Cloud; Crushing Coil; Regrow the Arm; Grasping Arms,
      // Lashing Arms, Crushing Arm, Lashing Arm
      if (/^drags |rise from/.test(n)) return 'grasp';
      if (/depths|whirl|tide|wave/.test(n)) return 'tide';
      if (/ink/.test(n)) return 'ink';
      if (/coil/.test(n)) return 'coil';
      if (/regrow|grow/.test(n)) return 'regrow';
      return 'grasp';
    case 'maw':
      // Lunge from the deep / Lunges on <name> (the card first); Swallow Whole, Frenzy of the Deep;
      // Reeling Tongue, Tongue Lash; Sink Below; Abyssal Roar; A Hundred Eyes; Ring of Teeth,
      // Gnashing Maw
      if (/^lunge/.test(n)) return 'gulp';
      if (/swallow|gulp|frenzy/.test(n)) return 'gulp';
      if (/tongue|reel/.test(n)) return 'tongue';
      if (/sink|below/.test(n)) return 'sink';
      if (/abyss|roar/.test(n)) return 'abyss';
      if (/eye/.test(n)) return 'eyes';
      return 'bite';
  }
}
